import React, {useCallback, useEffect, useRef, useState} from 'react';
import * as joint from '@joint/plus';
import Backdrop from '@mui/material/Backdrop';
import {CanvasActionType} from '../../../../@types/canvas-action-type';
import {FaultTreeViewType} from '../../../../@types/fault-tree-view-type';
import EventSequence from '../../../../@types/event-sequence';
import * as canvasActions from '../../../../utils/canvas/canvas-actions';
import * as canvasUtils from '../../../../utils/canvas/node-utils'
import {CanvasEnvironment} from '../../../../utils/canvas/canvas-environment';
import FaultTreeContextMenu from '../../../workspace/menus/FaultTreeContextMenu';
import {useWorkspaceContext} from '../../../../context/workspaceContext';
import {useProfiler} from '../../../../context/profilerContext';
import CircularProgress from '@mui/material/CircularProgress';
import AddEndEventModal from '../../modals/canvas/AddEndEvent.component';
import AddEventFaultTreeModal from '../../modals/canvas/AddEventFaultTree';
import AddFaultTreeNodeModal from '../../modals/canvas/AddFaultTreeNode.component';
import AddPivotalEventModal from '../../modals/canvas/AddPivotalEvent.component';
import DeleteEventModal from '../../modals/canvas/DeleteEvent.component';
import DeleteEventFaultTreeModal from '../../modals/canvas/DeleteEventFaultTree';
import DeleteFaultTreeNodeModal from '../../modals/canvas/DeleteFaultTreeNode.component';
import EditEndEventModal from '../../modals/canvas/EditEndEvent.component';
import EditInitiatingEventModal from '../../modals/canvas/EditInitiatingEvent.component';
import EditPivotalEventModal from '../../modals/canvas/EditPivotalEvent.component';
import EditEventFaultTreeModal from '../../modals/canvas/EditEventFaultTree';
import EditFaultTreeNodeModal from '../../modals/canvas/EditFaultTreeNode.component';
import ModelTestingPanel, {
    MODEL_TESTING_TAB_REVIEW, MODEL_TESTING_TAB_TEST_REPORT
} from '../../modals/canvas/ModelTesting.component';
import EventSequenceService from '../../../../services/event-sequence.service';
import QuantifyFaultTreeModal from '../../modals/canvas/QuantifyFaultTree.component';
import ReassignParentEventModal from '../../modals/canvas/ReassignParent.component';
import ReorderChildrenModal from '../../modals/canvas/ReorderChildren.component';
import {ResourceType} from '../../../../@types/resource-type';
import {SelectedWorkspaceObject} from '../../../../@types/workspace-types';
import {shouldRedrawCanvas} from './canvasRedraw';
import {toast} from 'react-toastify';
import TagsComponentModal from '../../modals/canvas/Tags.Component';
import CalloutComponentModal from '../../modals/canvas/Callout.component';
import DeleteCalloutComponentModal from '../../modals/canvas/DeleteCallout.component';
import OdnaCommentComponentModal from '../../modals/OdnaComment.component';
import AccidentDnaSummaryComponentModal from '../../modals/canvas/AccidentDnaSummary.component';
import PsfCodeSelectorModal from '../../modals/canvas/PsfCodeSelector.component';
import PsfCountTableModal from '../../modals/canvas/PsfCountTable.component';
import SelectComponentModal from '../../modals/canvas/SelectComponent.component';
import OdnaStatComponentModal from '../../modals/canvas/OdnaStat.component';
import NodeOccurrenceListComponentModal from '../../modals/canvas/NodeOccurrenceList.component';
import CFRLookUpNodeModal from '../../modals/canvas/CfrLookUp.component';
import MinimalCutSetsPanel from '../../modals/canvas/MinimalCutSets.component';
import SafetyModel from '../../../../@types/safety-model';

interface EventSequenceProps {
    eventSequence: EventSequence;
    isActive: boolean;
    readonly: boolean;
    updateTab: (tabObject: { objectType: ResourceType; object: EventSequence }) => void;
}

// Modal type enum for consolidated modal state management
type ModalType =
    | 'addEventFaultTree'
    | 'addEventFaultTreeNode'
    | 'addPivotalEvent'
    | 'addEndEvent'
    | 'editEventFaultTree'
    | 'editFaultTreeNode'
    | 'deleteEventFaultTree'
    | 'deleteFaultTreeNode'
    | 'editEndEvent'
    | 'editInitiatingEvent'
    | 'editPivotalEvent'
    | 'deleteEvent'
    | 'quantifyFaultTree'
    | 'reassignParent'
    | 'reorderChildren'
    | 'tags'
    | 'callout'
    | 'deleteCallout'
    | 'odnaComment'
    | 'odnaStat'
    | 'nodeOccurrenceList'
    | 'accidentDNASummary'
    | 'psfCodeSelector'
    | 'psfTableCount'
    | 'selectComponent'
    | 'cfrLookup'
    | 'minimalCutSets'
    | 'modelTesting';

type ModalState = Record<ModalType, boolean>;

const initialModalState: ModalState = {
    addEventFaultTree: false,
    addEventFaultTreeNode: false,
    addPivotalEvent: false,
    addEndEvent: false,
    editEventFaultTree: false,
    editFaultTreeNode: false,
    deleteEventFaultTree: false,
    deleteFaultTreeNode: false,
    editEndEvent: false,
    editInitiatingEvent: false,
    editPivotalEvent: false,
    deleteEvent: false,
    quantifyFaultTree: false,
    reassignParent: false,
    reorderChildren: false,
    tags: false,
    callout: false,
    deleteCallout: false,
    odnaComment: false,
    odnaStat: false,
    nodeOccurrenceList: false,
    accidentDNASummary: false,
    psfCodeSelector: false,
    psfTableCount: false,
    selectComponent: false,
    cfrLookup: false,
    minimalCutSets: false,
    modelTesting: false,
};

// Error handling utility for consistent error processing
const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
        // Handle Error objects (including AxiosError which extends Error)
        const axiosError = error as Error & { response?: { data?: { message?: string } } };
        if (axiosError.response?.data?.message) {
            return axiosError.response.data.message;
        }
        return error.message;
    }
    if (typeof error === 'string') {
        return error;
    }
    if (error && typeof error === 'object' && 'message' in error) {
        return String((error as { message: unknown }).message);
    }
    return 'An unexpected error occurred';
};

const handleError = (error: unknown, context?: string): void => {
    const message = getErrorMessage(error);
    const fullMessage = context ? `${context}: ${message}` : message;

    // eslint-disable-next-line no-console
    console.error('[JointJsCanvasEditor]', fullMessage, error);

    toast.error(fullMessage);
};

function AddChangesToGraph(eventSequence: EventSequence, canvasEnvironment: CanvasEnvironment): number {
    // Save the center of the visible area in paper coordinates to restore if no selection
    const savedViewCenter = canvasEnvironment.paperScroller.getVisibleArea().center();

    const collapsedIds = [];
    if (canvasEnvironment.activeTopNode) {
        const [topFaultTreeNode] = canvasEnvironment.graph.getNeighbors(canvasEnvironment.activeTopNode).filter((cell) => cell.get('nodeClass') === 'FAULTTREENODE');
        const faultTreeBranch = canvasEnvironment.graph.getSubgraph([canvasEnvironment.activeTopNode, topFaultTreeNode, ...canvasEnvironment.graph.getSuccessors(topFaultTreeNode)]);
        collapsedIds.push(...faultTreeBranch.filter((cell) => cell.isElement() && cell.get('collapsed')).map((cell) => cell.id));
        if (!collapsedIds.includes(canvasEnvironment.activeTopNode.id)) {
            collapsedIds.splice(0,0,canvasEnvironment.activeTopNode.id);
        }
    }
    canvasEnvironment.activeTopNode = null;

    let { left, top, parentLeft, parentTop } = (canvasEnvironment.selection.length > 0) ?
        canvasActions.getTopLeftPaddingOfElementAndParent(canvasEnvironment, canvasEnvironment.selection[0]) :
        { left: 50, top: 50, parentLeft: 50, parentTop: 50 };
    const parentIds = canvasEnvironment.selection.map((cell) => canvasEnvironment.graph.getPredecessors(cell)[0]?.id ).filter((id) => !!id);
    const ids = canvasEnvironment.selection.map((cell) => cell.id);
    canvasActions.setSelection(canvasEnvironment, []);
    // Always add tools here since AddChangesToGraph is only called when tab is active
    const rafId = importGraphFromObject(eventSequence, canvasEnvironment, false, true);
    // Set flag to indicate we're restoring state (skip loading spinner during expand)
    canvasEnvironment.isRestoringState = true;
    collapsedIds.forEach((id) => {
        const cell = canvasEnvironment.graph.getCell(id);
        if (cell && cell.isElement()) {
            // Skip nodes with hasUnloadedChildren - these should be handled by
            // buildftGraphFromObject using expandedLazyNodeIds, not by expand trigger
            if (cell.get('hasUnloadedChildren')) {
                return;
            }
            const cellView = canvasEnvironment.paper.findViewByModel(cell);
            if (cellView) {
                canvasEnvironment.paper.trigger('element:expand', cellView);
            }
        }
    });
    canvasEnvironment.isRestoringState = false;
    const selection: Array<joint.dia.Element> = [];
    ids.forEach((id) => {
        const cell = canvasEnvironment.graph.getCell(id);
        if (cell && cell.isElement()) {
            selection.push(cell);
        }
    });

    // Positionning graph view to where it was before changes
    const graphBBox = canvasEnvironment.graph.getBBox();
    if (graphBBox && graphBBox.containsPoint(savedViewCenter)) {
        // Saved center is still within graph bounds
        canvasEnvironment.paperScroller.center(savedViewCenter.x, savedViewCenter.y);
    } else  if (selection.length > 0) {
        if (canvasEnvironment.paperScroller.isElementVisible(selection[0])) {
            canvasEnvironment.paperScroller.positionElement(selection[0], 'top-left', { padding: { top: top, left: left } });
        } else {
            canvasEnvironment.paperScroller.positionElement(selection[0], 'top-left', { padding: { top: 50, left: left } });
        }
    } else if (parentIds.length > 0 && parentIds[0] != null) {
        const parent = canvasEnvironment.graph.getCell(parentIds[0]);
        if (parent && parent.isElement()) {
            selection.push(parent);
            if (canvasEnvironment.paperScroller.isElementVisible(parent)) {
                canvasEnvironment.paperScroller.positionElement(parent, 'top-left', { padding: { top: parentTop, left: parentLeft } });
            } else {
                canvasEnvironment.paperScroller.positionElement(parent, 'top-left', { padding: { top: 50, left: parentLeft } });
            }
        }
    } else {
        // on last resort, center on source element
        const [source] = canvasEnvironment.graph.getSources();
        if (source) {
            canvasEnvironment.paperScroller.centerElement(source);
        }
    }
    canvasActions.setSelection(canvasEnvironment, selection);
    canvasEnvironment.isDirty = false;

    return rafId;
}

function importGraphFromObject(eventSequence: EventSequence, canvasEnvironment: CanvasEnvironment, positioning: boolean = true, shouldAddTools: boolean = true): number {
    const { graph, paper, paperScroller } = canvasEnvironment;
    canvasEnvironment.eventSequence = eventSequence;
    paper.freeze();

    // Get the current view strategy
    const viewStrategy = canvasEnvironment.currentViewStrategy;

    // Build ESD graph using strategy
    viewStrategy.buildEsdGraph(eventSequence, graph);

    // Get ESD layout options and create layout
    const esdLayoutOptions = viewStrategy.getEsdLayoutOptions();
    const esdTreeLayout = viewStrategy.createLayout(graph, esdLayoutOptions);

    // Get source and set collapsed state for all ESD nodes
    const [source] = graph.getSources() as joint.dia.Element[];
    graph.getSuccessors(source).forEach((cell) => {
        if (cell.get('nodeClass') !== 'FAULTTREENODE') {
            cell.set({ collapsed: true });
        }
    });
    source.set({ collapsed: true });

    // Apply layout
    const viewportOverlap = 50;
    const viewportRect: joint.g.Rect = paperScroller.getVisibleArea().inflate(viewportOverlap);
    canvasActions.layoutAndFocus(esdTreeLayout, paperScroller, paper, viewportRect.center());

    // Apply post-processing (e.g., dendogram conversion)
    viewStrategy.applyEsdPostProcessing(graph, source);

    paper.unfreeze();

    // Defer positioning and decorations until viewport has settled after unfreeze
    const rafId = requestAnimationFrame(() => {
        // Guard: check if paper is still valid (not destroyed)
        if (!paper.el || !paper.el.parentNode) return;

        // Position the source element after DOM has settled (only on initial load)
        if (positioning) {
            paperScroller.centerElement(source);
        }

        // Clear tracking since this is a fresh load
        canvasEnvironment.clearDecorationTracking();

        // Fetch tags first if tags mode is active, then decorate
        if (canvasEnvironment.isTagsModeActive) {
            canvasEnvironment.fetchTagsAndTagLinks().then(() => {
                // Batch all decorations in a single pass for visible elements only
                canvasActions.decorateVisibleElements(canvasEnvironment, { addToolsFlag: shouldAddTools });
                canvasActions.drawDnaPath(canvasEnvironment);
            }).catch((error: unknown) => {
                handleError(error, 'Failed to fetch tags');
                // Still decorate other elements even if tags fail
                canvasActions.decorateVisibleElements(canvasEnvironment, { addToolsFlag: shouldAddTools });
                canvasActions.drawDnaPath(canvasEnvironment);
            });
        } else {
            // Batch all decorations in a single pass for visible elements only
            canvasActions.decorateVisibleElements(canvasEnvironment, { addToolsFlag: shouldAddTools });
            canvasActions.drawDnaPath(canvasEnvironment);
        }
    });

    canvasEnvironment.selection = [];
    canvasEnvironment.isDirty = false;

    return rafId;
}


export default function JointJsCanvasEditor(props: EventSequenceProps): React.ReactElement {
    const containerRef = useRef(null);
    const canvasEnvironmentRef = useRef<CanvasEnvironment | null>(null);
    const decorationRafRef = useRef<number | null>(null);
    /** The selected object whose request to redraw the canvas has been answered. */
    const redrawAnsweredRef = useRef<SelectedWorkspaceObject | null>(null);
    const { eventSequence, isActive, readonly, updateTab } = props;
    const [isRunning, setIsRunning] = React.useState<boolean>(false);
    const [anchorEl, setAnchorEl] = React.useState<null | joint.dia.ElementView>(null);
    const [canvasEnvironment, setCanvasEnvironment] = React.useState<CanvasEnvironment | null>(null);
    const [modals, setModals] = React.useState<ModalState>(initialModalState);
    const [isCanvasFullscreen, setIsCanvasFullscreen] = useState<boolean>(false);

    // MCS bottom panel state
    const MCS_HEADER_HEIGHT = 40;
    const MCS_MIN_HEIGHT = 150;
    const MCS_DEFAULT_HEIGHT = 250;
    const [mcsPanelHeight, setMcsPanelHeight] = useState<number>(MCS_DEFAULT_HEIGHT);
    const [mcsPanelCollapsed, setMcsPanelCollapsed] = useState<boolean>(false);
    const [mcsResetKey, setMcsResetKey] = useState<number>(0);
    const [mcsInitialTab, setMcsInitialTab] = useState<number>(0);
    const [modelTestingTab, setModelTestingTab] = useState<number>(MODEL_TESTING_TAB_REVIEW);
    const resizeRef = useRef<{ startY: number; startHeight: number } | null>(null);

    const handleMcsResizeMouseDown = useCallback((e: React.MouseEvent): void => {
        e.preventDefault();
        resizeRef.current = { startY: e.clientY, startHeight: mcsPanelHeight };

        const handleMouseMove = (moveEvent: MouseEvent): void => {
            if (!resizeRef.current) return;
            const maxHeight = window.innerHeight * 0.5;
            const newHeight = resizeRef.current.startHeight + (resizeRef.current.startY - moveEvent.clientY);
            setMcsPanelHeight(Math.max(MCS_MIN_HEIGHT, Math.min(maxHeight, newHeight)));
        };

        const handleMouseUp = (): void => {
            resizeRef.current = null;
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [mcsPanelHeight]);

    const mcsOpen = modals.minimalCutSets && canvasEnvironment != null;

    // Helper functions for modal management
    const openModal = (modal: ModalType): void => {
        setModals(prev => ({ ...prev, [modal]: true }));
    };
    const closeModal = (modal: ModalType): void => {
        setModals(prev => ({ ...prev, [modal]: false }));
    };
    const { currentWorkspaceObject, workspaceObjects, setWorkspaceObjects,  getAdditionalResource, currentCanvasAction, setCurrentCanvasAction, setCurrentCanvasSelectedNode,
        setCurrentWorkspaceObject, publishResourceUpdate, setCanvasSearchFunction, setCanvasSearchByTagFunction, setCanvasNavigateFunction,
        lazyLoadDepth, loadAllFT, currentFtViewType } = useWorkspaceContext();
    const open = Boolean(anchorEl);
    const openContextMenu = useCallback((event: joint.dia.Event, targetElement: joint.dia.ElementView): void => {
        event.preventDefault();
        event.stopPropagation();
        if (readonly) return;
        setAnchorEl(targetElement);
    }, []);
    const closeContextMenu = useCallback((): void => {
        setAnchorEl(null);
    }, []);

    const displayPsf = (service: CanvasEnvironment, elementView: joint.dia.ElementView): void => {
        if (!service) return;
        const parent = service.graph.getNeighbors(elementView.model, { inbound: true })[0];
        canvasActions.setSelection(service, [parent]);
        openModal('psfTableCount');
    }


    const handleProperties = (): void => {
        if (!canvasEnvironment) return;
        const [element] = canvasEnvironment.selection;
        if (!element) return;
        const type = element.get('data').type;
        if (type === 'PIVOTAL') {
            setCurrentCanvasAction({ actionType: CanvasActionType.EVENTNODE_EDIT_PIVOTALEVENT });
        } else if (type === 'INITIATING') {
            setCurrentCanvasAction({ actionType: CanvasActionType.EVENTNODE_EDIT_INITIATINGEVENT });
        } else if (type === 'END') {
            setCurrentCanvasAction({ actionType: CanvasActionType.EVENTNODE_EDIT_ENDEVENT });
        } else if (type === 'INPRECURSOR') {
            setCurrentCanvasAction({ actionType: CanvasActionType.EVENTNODE_EDIT_INPRECURSOREVENT });
        } else if (canvasUtils.isFaultTreeNode(type)) {
            setCurrentCanvasAction({ actionType: CanvasActionType.FAULTTREENODE_EDIT_NODE });
        }
    }

    const handleNodeDeleltion = (): void => {
        if (!canvasEnvironment) return;
        const [element] = canvasEnvironment.selection;
        if (!element) return;
        const type = element.get('data').type;
        if (type === 'PIVOTAL' || type === 'INITIATING' || type === 'END' || type === 'INPRECURSOR') {
            openModal('deleteEvent');
        } else if (canvasUtils.isFaultTreeNode(type)) {
            openModal('deleteFaultTreeNode');
        }
    }

    const publishEventSequenceUpdate = (eventSequence: EventSequence): void => {
        // Publish update event for other components
        publishResourceUpdate({
            type: ResourceType.EVENT_SEQUENCES,
            id: eventSequence.id,
            updatedData: eventSequence,
            source: 'save'
        });
    };

    // Helper for async service operations with loading state and error handling
    const withAsyncOperation = <T,>(
        operation: () => Promise<T>,
        onSuccess: (result: T) => void,
        successMessage?: string,
        errorContext?: string
    ): void => {
        setIsRunning(true);
        operation()
            .then((result) => {
                onSuccess(result);
                if (successMessage) {
                    toast.success(successMessage);
                }
            })
            .catch((error: unknown) => {
                handleError(error, errorContext);
            })
            .finally(() => {
                setIsRunning(false);
            });
    };

    // Helper for sync operations that need loading state (for UI feedback)
    const withSyncOperation = (operation: () => void, errorContext?: string): void => {
        setIsRunning(true);
        try {
            operation();
        } catch (error: unknown) {
            handleError(error, errorContext);
        } finally {
            setIsRunning(false);
        }
    };

    const updateFromUndoRedo = (eventSequence: EventSequence): void => {
        let eventSequenceObject = { objectType:ResourceType.EVENT_SEQUENCE_TREES, object: eventSequence };
        setCurrentWorkspaceObject(eventSequenceObject);
        if (workspaceObjects?.objectType === ResourceType.SAFETY_MODELS) {
            let resourcesObject = getAdditionalResource(ResourceType.SAFETY_MODELS);
            if (resourcesObject && resourcesObject.resources) {
                let resources = [...resourcesObject.resources] as SafetyModel[];
                let sm = resources.find((resource) => resource.id == eventSequence.containerId);
                if (sm) {
                    if (!sm.children) {
                        sm.children = [];
                    }
                    let index = sm.children.findIndex((es) => es.id === eventSequence.id);
                    if (index !== -1) {
                        sm.children[index] = eventSequence;
                    }
                    setWorkspaceObjects({ objectType: ResourceType.SAFETY_MODELS, resources: resources });
                }
            } 
        } 
        updateTab(eventSequenceObject);
    }

    
    useEffect(() => {
        if (isActive && currentCanvasAction!== null && currentCanvasAction.actionType !== CanvasActionType.UNKNOWN) {
            if (!canvasEnvironment) return;
            switch (currentCanvasAction.actionType) {
                case CanvasActionType.CANVAS_RECENTER:
                    canvasActions.recenter(canvasEnvironment);
                    break;
                case CanvasActionType.CANVAS_FIT_TO_VIEW:
                    setIsRunning(true);
                    setTimeout(() => {
                        canvasActions.fitToView(canvasEnvironment);
                        setIsRunning(false);
                    }, 0);
                    break;
                case CanvasActionType.CANVAS_ZOOM_IN:
                    canvasActions.zoomIn(canvasEnvironment);
                    break;
                case CanvasActionType.CANVAS_ZOOM_OUT:
                    canvasActions.zoomOut(canvasEnvironment);
                    break;
                case CanvasActionType.CANVAS_FULLSCREEN:
                    canvasActions.fullScreen(canvasEnvironment);
                    break;
                case CanvasActionType.CANVAS_RELOAD_FT:
                    setMcsResetKey((k) => k + 1);
                    if (canvasEnvironment && canvasEnvironment.activeTopNode) {
                        // Update depth from context
                        canvasEnvironment.lazyLoadDepth = lazyLoadDepth;
                        canvasEnvironment.loadAllFT = loadAllFT;
                        // Collapse and re-expand the current FT to apply new depth
                        const activeTopNode = canvasEnvironment.activeTopNode;
                        const cellView = canvasEnvironment.paper.findViewByModel(activeTopNode);
                        if (cellView) {
                            // Show loading spinner
                            setIsRunning(true);
                            // Collapse first
                            canvasEnvironment.paper.trigger('element:expand', cellView);
                            // Then expand with new depth (use setTimeout to allow spinner to render)
                            setTimeout(() => {
                                canvasEnvironment.paper.trigger('element:expand', cellView);
                                // Spinner will be hidden by the expand handler when done
                            }, 0);
                        }
                    }
                    break;
                case CanvasActionType.CANVAS_SWITCH_FT_VIEW:
                    setMcsResetKey((k) => k + 1);
                    if (canvasEnvironment && currentCanvasAction.query?.viewType) {
                        const viewType = currentCanvasAction.query.viewType as FaultTreeViewType;
                        canvasEnvironment.setViewType(viewType);

                        // Show loading spinner
                        setIsRunning(true);

                        // Rebuild canvas from scratch for view switch
                        setTimeout(() => {
                            const { graph, paper, paperScroller } = canvasEnvironment;
                            paper.freeze();

                            // Clear existing graph
                            graph.clear();

                            // Get the current view strategy (after setViewType)
                            const viewStrategy = canvasEnvironment.currentViewStrategy;

                            // Build ESD graph using strategy
                            viewStrategy.buildEsdGraph(canvasEnvironment.eventSequence!, graph);

                            // Get ESD layout options and create layout
                            const esdLayoutOptions = viewStrategy.getEsdLayoutOptions();
                            const esdLayout = viewStrategy.createLayout(graph, esdLayoutOptions);

                            // Set collapsed state for all ESD nodes
                            const [source] = graph.getSources();
                            if (source) {
                                graph.getSuccessors(source).forEach((cell) => {
                                    if (cell.get('nodeClass') !== 'FAULTTREENODE') {
                                        cell.set({ collapsed: true });
                                    }
                                });
                                source.set({ collapsed: true });
                            }

                            // Apply layout and center on the source element (IE)
                            esdLayout.layout();
                            canvasActions.resizePaper(paper);

                            // Apply post-processing (e.g., dendogram conversion)
                            viewStrategy.applyEsdPostProcessing(graph, source);

                            // Center on the source element (IE) after view switch
                            if (source) {
                                paperScroller.centerElement(source);
                            }

                            // Reset active top node since we're starting fresh
                            canvasEnvironment.activeTopNode = null;

                            paper.unfreeze();

                            // Add tools after unfreeze - use RAF to ensure DOM is ready
                            requestAnimationFrame(() => {
                                // Guard: check if paper is still valid
                                if (!paper.el || !paper.el.parentNode) {
                                    setIsRunning(false);
                                    return;
                                }

                                // Clear decoration tracking for fresh tool addition
                                canvasEnvironment.clearDecorationTracking();

                                // Add tools to all elements (skip viewport filter to ensure all get tools)
                                canvasActions.decorateVisibleElements(canvasEnvironment, {
                                    addToolsFlag: true,
                                    skipViewportFilter: true
                                });

                                setIsRunning(false);
                            });
                        }, 0);
                    }
                    break;
                case CanvasActionType.CANVAS_SEARCH:
                    if (currentCanvasAction.query)
                        canvasActions.search(canvasEnvironment, currentCanvasAction.query);
                    break;
                case CanvasActionType.CANVAS_TOGGLE_TAGS:
                    canvasActions.toggleTagsMode(canvasEnvironment);
                    break;
                case CanvasActionType.CANVAS_TOGGLE_CALLOUTS:
                    canvasActions.toggleCalloutsMode(canvasEnvironment);
                    break;
                case CanvasActionType.CANVAS_TOGGLE_ODNA:
                    canvasActions.toggleOdnaMode(canvasEnvironment);
                    break;
                case CanvasActionType.CANVAS_REDO:
                    withAsyncOperation(
                        () => EventSequenceService.redo(canvasEnvironment.eventSequence!.id),
                        (response) => {
                            canvasEnvironment.isDirty = true;
                            updateFromUndoRedo(response);
                            publishEventSequenceUpdate(response);
                            setCurrentWorkspaceObject({ objectType: ResourceType.EVENT_SEQUENCE_TREES, object: response });
                        },
                        'Redo completed successfully'
                    );
                    break;
                case CanvasActionType.CANVAS_UNDO:
                    withAsyncOperation(
                        () => EventSequenceService.undo(canvasEnvironment.eventSequence!.id),
                        (response) => {
                            canvasEnvironment.isDirty = true;
                            updateFromUndoRedo(response);
                            publishEventSequenceUpdate(response);
                            setCurrentWorkspaceObject({ objectType: ResourceType.EVENT_SEQUENCE_TREES, object: response });
                        },
                        'Undo completed successfully'
                    );
                    break;
                case CanvasActionType.CANVAS_REFRESH:
                    if (canvasEnvironment.eventSequence) {
                        withAsyncOperation(
                            () => EventSequenceService.getEventSequenceById(canvasEnvironment.eventSequence!.id),
                            (response) => {
                                canvasEnvironment.isDirty = true;
                                setCurrentWorkspaceObject({
                                    objectType: ResourceType.EVENT_SEQUENCE_TREES,
                                    object: response
                                });
                            }
                        );
                    }
                    break;
                case CanvasActionType.NODE_PROPERTIES:
                    handleProperties();
                    break;
                case CanvasActionType.NODE_CFR_LOOKUP:
                    openModal('cfrLookup');
                    break;
                case CanvasActionType.NODE_ADD_CALLOUT:
                case CanvasActionType.NODE_EDIT_CALLOUT:
                    openModal('callout');
                    break;
                case CanvasActionType.NODE_DELETE_CALLOUT:
                    openModal('deleteCallout');
                    break;
                case CanvasActionType.NODE_TAGS:
                    openModal('tags');
                    break;
                case CanvasActionType.NODE_PSF:
                    openModal('psfCodeSelector');
                    break;
                case CanvasActionType.NODE_PSF_COUNTS:
                    openModal('psfTableCount');
                    break;
                case CanvasActionType.CANVAS_PSF_TEMPLATE:
                    canvasEnvironment.setPsfTemplate(currentCanvasAction.query);
                    break;
                case CanvasActionType.CANVAS_ODNA_STATS:
                    canvasEnvironment.setEventSequenceOccurrenceStats(currentCanvasAction.query);
                    break;           
                case CanvasActionType.CANVAS_ODNA_CLEAR_STATS:
                    canvasEnvironment.clearEventSequenceOccurrenceStats();
                    break;           
                case CanvasActionType.CANVAS_PSF_COUNTS:
                    withSyncOperation(() => canvasEnvironment.setPsfCounts(currentCanvasAction.query));
                    break;
                case CanvasActionType.CANVAS_CLEAR_PSF:
                    withSyncOperation(() => canvasActions.clearPsfMode(canvasEnvironment));
                    break;
                case CanvasActionType.EVENT_SEQUENCE_SENSITIVITY_ANALYSIS:
                    setCurrentWorkspaceObject({ objectType: ResourceType.EVENT_SEQUENCE_SENSITIVIY_ANALYSIS, object: canvasEnvironment.eventSequence });
                    break;
                case CanvasActionType.EVENT_SEQUENCE_UNCERTAINTY_ANALYSIS:
                    setCurrentWorkspaceObject({ objectType: ResourceType.EVENT_SEQUENCE_UNCERTAINTY_ANALYSIS, object: canvasEnvironment.eventSequence });
                    break;
                case CanvasActionType.CANVAS_IRP_SELECT:
                    withSyncOperation(() => canvasEnvironment.setIrp(currentCanvasAction.query));
                    break;
                case CanvasActionType.CANVAS_PULLED_ODNA:
                    withSyncOperation(() => canvasEnvironment.setLinkedOccurrenceDna(currentCanvasAction.query));
                    break;
                case CanvasActionType.CANVAS_STATUS_CHANGE_ODNA:
                    withSyncOperation(() => canvasEnvironment.setStatusLinkedOccurrenceDna(currentCanvasAction.query));
                    break;
                case CanvasActionType.INCIDENT_DNA_FAILURE:
                    withSyncOperation(() => canvasEnvironment.setODNAStatusOfCurrentSelectedNode('FAILURE'));
                    break;
                case CanvasActionType.INCIDENT_DNA_SUCCESS:
                    withSyncOperation(() => canvasEnvironment.setODNAStatusOfCurrentSelectedNode('SUCCESS'));
                    break;
                case CanvasActionType.INCIDENT_DNA_CLEAR:
                    withSyncOperation(() => canvasEnvironment.setODNAStatusOfCurrentSelectedNode('CLEAR'));
                    break;
                case CanvasActionType.CANVAS_SAVE_ODNA:
                    // Save the linked occurrence dna to the occurrence with the id in the query
                    withSyncOperation(() => canvasEnvironment.saveLinkedOccurrenceDna(currentCanvasAction.query));
                    break;
                case CanvasActionType.CANVAS_CLEAR_ODNA:
                    withSyncOperation(() => canvasEnvironment.clearLinkedOccurrenceDna());
                    
                    break;
                case CanvasActionType.INCIDENT_DNA_COMMENTS:
                    openModal('odnaComment');
                    break;
                case CanvasActionType.INCIDENT_DNA_STATISTICS:
                    openModal('odnaStat');
                    break;
                case CanvasActionType.NODE_OCCURRENCE_LIST_PARAMS:
                    openModal('nodeOccurrenceList');
                    break;
                case CanvasActionType.INCIDENT_DNA_STATISTIC_SUMMARY:
                    openModal('accidentDNASummary');
                    break;
                case CanvasActionType.EVENTNODE_ADD_PIVOTALEVENT:
                    openModal('addPivotalEvent');
                    break;
                case CanvasActionType.EVENTNODE_ADD_ENDEVENT:
                    openModal('addEndEvent');
                    break;
                case CanvasActionType.EVENTNODE_DELETE:
                case CanvasActionType.FAULTTREENODE_DELETE_NODE:
                    handleNodeDeleltion();
                    break;
                case CanvasActionType.EVENTNODE_EDIT_PIVOTALEVENT:
                    openModal('editPivotalEvent');
                    break;
                case CanvasActionType.EVENTNODE_EDIT_INITIATINGEVENT:
                    openModal('editInitiatingEvent');
                    break;
                case CanvasActionType.EVENTNODE_EDIT_ENDEVENT:
                    openModal('editEndEvent');
                    break;
                case CanvasActionType.EVENTNODE_REASSIGN_PARENT:
                    openModal('reassignParent');
                    break;
                case CanvasActionType.EVENTNODE_COPY:
                    if (canvasEnvironment.selection.length > 0) {
                        const [copiedNode] = canvasEnvironment.selection;
                        localStorage.copyContents = JSON.stringify({
                            type: 'event',
                            id: copiedNode.get('data').id
                        });
                    }
                    break;
                case CanvasActionType.EVENTNODE_PASTE:
                    if (
                        canvasEnvironment.selection.length > 0 &&
                        canvasActions.canPasteEvent(canvasEnvironment, canvasEnvironment.selection[0].get('data'))
                    ) {
                        const children = canvasEnvironment.graph.getNeighbors(canvasEnvironment.selection[0], { outbound: true })
                            .filter((cell) => cell.get('nodeClass') !== 'CALLOUTNODE' && cell.get('nodeClass') !== 'PSFNODE');
                        const parentRelationType = (children && children.length === 1 && children[0].get('data').parentRelationType === 'YES') ? 'NO' : 'YES';
                        withAsyncOperation(
                            () => EventSequenceService.copyEvent(
                                JSON.parse(localStorage.copyContents).id,
                                canvasEnvironment.selection[0].get('data').id,
                                parentRelationType
                            ),
                            (response) => {
                                canvasEnvironment.isDirty = true;
                                publishEventSequenceUpdate(response);
                                setCurrentWorkspaceObject({ objectType: ResourceType.EVENT_SEQUENCE_TREES, object: response });
                            },
                            'Event copied successfully'
                        );
                    }
                    break;
                case CanvasActionType.EVENTNODE_FAULTTREE_ADD:
                    if (canvasEnvironment.selection.length > 0) {
                        const eventId = canvasEnvironment.selection[0].get('data').id;
                        withAsyncOperation(
                            () => EventSequenceService.addEventFaultTree(eventId),
                            (response) => {
                                canvasEnvironment.isDirty = true;
                                publishEventSequenceUpdate(response);
                                setCurrentWorkspaceObject({ objectType: ResourceType.EVENT_SEQUENCE_TREES, object: response });
                            }
                        );
                    }
                    break;
                case CanvasActionType.EVENTNODE_FAULTTREE_DELETE:
                    openModal('deleteEventFaultTree');
                    break;
                case CanvasActionType.EVENTNODE_FAULTTREE_PROPERTIES:
                    openModal('editEventFaultTree');
                    break;
                case CanvasActionType.EVENTNODE_FAULTTREE_COPY:
                    if (canvasEnvironment.selection.length > 0) {
                        const [copiedNode] = canvasEnvironment.selection;
                        localStorage.copyContents = JSON.stringify({
                            type: 'fault_tree',
                            id: copiedNode.get('data').faultTree.id,
                            containPrecursor: copiedNode.get('data').faultTree.faultTreeNodes.some((node: any) => node.nodeClass === 'PRECURSOR')
                        });
                    }
                    break;
                case CanvasActionType.EVENTNODE_FAULTTREE_PASTE:
                    if (
                        canvasEnvironment.selection.length > 0 &&
                        canvasActions.canPasteEventFaultTree(canvasEnvironment, canvasEnvironment.selection[0].get('data'))
                    ) {
                        withAsyncOperation(
                            () => EventSequenceService.copyEventFaultTree(
                                JSON.parse(localStorage.copyContents).id,
                                canvasEnvironment.selection[0].get('data').id
                            ),
                            (response) => {
                                canvasEnvironment.isDirty = true;
                                publishEventSequenceUpdate(response);
                                setCurrentWorkspaceObject({ objectType: ResourceType.EVENT_SEQUENCE_TREES, object: response });
                            },
                            'Event Fault Tree copied successfully'
                        );
                    }
                    break;
                case CanvasActionType.FAULTTREENODE_ADD_NODE:
                    openModal('addEventFaultTreeNode');
                    break;
                case CanvasActionType.FAULTTREENODE_COPY_SUBTREE:
                    if (canvasEnvironment.selection.length > 0) {
                        const [copiedNode] = canvasEnvironment.selection;
                        localStorage.copyContents = JSON.stringify({
                            type: 'fault_tree_node',
                            id: copiedNode.get('data').id
                        });
                    }
                    break;            
                case CanvasActionType.FAULTTREENODE_EDIT_NODE:
                    openModal('editFaultTreeNode');
                    break;
                case CanvasActionType.FAULTTREENODE_ADD_COMPONENT:
                    openModal('selectComponent');
                    break;
                case CanvasActionType.FAULTTREENODE_PASTE_SUBTREE:
                case CanvasActionType.FAULTTREENODE_PASTE_COMPONENT:
                    if (
                        canvasActions.canAddOrEditResource(canvasEnvironment) &&
                        canvasEnvironment.selection.length > 0 &&
                        canvasUtils.canPasteFaultTreeNode(canvasEnvironment.selection[0].get('data'))
                    ) {
                        withAsyncOperation(
                            () => EventSequenceService.copyEventFaultTreeNode(
                                JSON.parse(localStorage.copyContents).id,
                                canvasEnvironment.selection[0].get('data').id
                            ),
                            (response) => {
                                canvasEnvironment.isDirty = true;
                                publishEventSequenceUpdate(response);
                                setCurrentWorkspaceObject({ objectType: ResourceType.EVENT_SEQUENCE_TREES, object: response });
                            },
                            'Operation completed successfully'
                        );
                    }
                    break;
                case CanvasActionType.FAULTTREENODE_REORDER_CHILDREN:
                    openModal('reorderChildren');
                    break;
                case CanvasActionType.FAULTTREENODE_MINIMAL_CUT_SETS:
                    setMcsInitialTab(0);
                    openModal('minimalCutSets');
                    break;
                case CanvasActionType.FAULTTREENODE_CCF_ANALYSIS:
                    setMcsInitialTab(1);
                    openModal('minimalCutSets');
                    break;
                case CanvasActionType.NODE_QUANTIFY_CHILDREN:
                case CanvasActionType.NODE_QUANTIFY_BOTTOM_UP:
                case CanvasActionType.NODE_QUANTIFY_TOP_DOWN:
                case CanvasActionType.NODE_QUANTIFY_BOOTOM_UP_TO_HERE:
                    openModal('quantifyFaultTree');
                    break;
                case CanvasActionType.TEST_REPORT_MAPPING:
                    setModelTestingTab(MODEL_TESTING_TAB_TEST_REPORT);
                    openModal('modelTesting');
                    break;
                case CanvasActionType.MODEL_TESTING:
                    setModelTestingTab(MODEL_TESTING_TAB_REVIEW);
                    openModal('modelTesting');
                    break;
                case CanvasActionType.SANITIZE_EVENT_SEQUENCE:
                    if (!canvasEnvironment.eventSequence) return;
                    withAsyncOperation(
                        () => EventSequenceService.sanitizeEventSequence(canvasEnvironment.eventSequence!.id),
                        (response) => {
                            canvasEnvironment.isDirty = true;
                            publishEventSequenceUpdate(response);
                            setCurrentWorkspaceObject({ objectType: ResourceType.EVENT_SEQUENCE_TREES, object: response });
                        },
                        'Safety View sanitized successfully'
                    );
                    break;
                case CanvasActionType.SANITIZE_OCCURRENCE_DNA:
                    if (!canvasEnvironment.eventSequence) return;
                    withAsyncOperation(
                        () => EventSequenceService.sanitizeOccurrenceDna(canvasEnvironment.eventSequence!.id),
                        (response) => {
                            canvasEnvironment.isDirty = true;
                            publishEventSequenceUpdate(response);
                            setCurrentWorkspaceObject({ objectType: ResourceType.EVENT_SEQUENCE_TREES, object: response });
                        },
                        'Occurrence DNA sanitized successfully'
                    );
                    break;
                default:
                    break;
            }

            setTimeout(() => {
                setCurrentCanvasAction({ actionType: CanvasActionType.UNKNOWN });
            }, 100);
        }
    }, [currentCanvasAction, isActive, canvasEnvironment]);


    // Canvas initialization - single point of creation to avoid race conditions
    useEffect(() => {
        if (!containerRef || !containerRef.current) return;
        if (!eventSequence || !eventSequence.events) return;

        // Only create canvas environment once
        if (canvasEnvironmentRef.current === null) {
            canvasEnvironmentRef.current = new CanvasEnvironment(
                containerRef.current,
                openContextMenu,
                setCurrentCanvasSelectedNode,
                setIsRunning,
                readonly
            );
            setCanvasEnvironment(canvasEnvironmentRef.current);
            canvasEnvironmentRef.current.fullscreenCallback = (): void => {
                setIsCanvasFullscreen((prev) => !prev);
            };
            canvasEnvironmentRef.current.paper.on('element:psftablecount', (elementView: joint.dia.ElementView) => {
                displayPsf(canvasEnvironmentRef.current!, elementView);
            });
        }

        const env = canvasEnvironmentRef.current;
        if (!env.eventSequence || env.eventSequence.id === 0) {
            decorationRafRef.current = importGraphFromObject(eventSequence, env);
        }

        // Cleanup on unmount
        return (): void => {
            // Cancel any pending RAF to prevent callbacks on unmounted components
            if (decorationRafRef.current !== null) {
                cancelAnimationFrame(decorationRafRef.current);
                decorationRafRef.current = null;
            }
            if (canvasEnvironmentRef.current === null) return;
            canvasEnvironmentRef.current.destroy();
            canvasEnvironmentRef.current = null;
            setCurrentCanvasSelectedNode(null);
        };
    }, [eventSequence, openContextMenu, readonly]);

    // Handle CSS fullscreen: Escape key to exit and viewport resize
    useEffect(() => {
        if (!isCanvasFullscreen) return undefined;

        const handleKeyDown = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') {
                setIsCanvasFullscreen(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden';

        // Trigger viewport recalculation after layout settles
        requestAnimationFrame(() => {
            canvasEnvironmentRef.current?.paperScroller.adjustPaper();
        });

        return (): void => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
            // Recalculate viewport when exiting fullscreen
            requestAnimationFrame(() => {
                canvasEnvironmentRef.current?.paperScroller.adjustPaper();
            });
        };
    }, [isCanvasFullscreen]);

    // Check for new changes
    useEffect(() => {
        if (isActive && canvasEnvironment && canvasEnvironment.eventSequence && canvasEnvironment.eventSequence.id !== 0) {
            // A change made outside the canvas never marks it dirty, so it asks for the redraw itself,
            // once: see shouldRedrawCanvas.
            if (currentWorkspaceObject && shouldRedrawCanvas(currentWorkspaceObject, canvasEnvironment.eventSequence.id,
                Boolean(canvasEnvironment.isDirty), redrawAnsweredRef.current)) {
                if (currentWorkspaceObject.redrawCanvas) {
                    redrawAnsweredRef.current = currentWorkspaceObject;
                }
                decorationRafRef.current = AddChangesToGraph(currentWorkspaceObject.object, canvasEnvironment);
            }
        }
    }, [isActive, currentWorkspaceObject])

    // Sync lazy load settings from context to canvasEnvironment
    useEffect(() => {
        if (canvasEnvironment) {
            canvasEnvironment.lazyLoadDepth = lazyLoadDepth;
            canvasEnvironment.loadAllFT = loadAllFT;
            // Update view type without triggering lifecycle hooks (that happens on action)
            canvasEnvironment.currentViewType = currentFtViewType;

            // When tab becomes active, check if view type changed while inactive
            // If so, update tools to match the current view type
            if (isActive && canvasEnvironment.renderedViewType !== null &&
                canvasEnvironment.renderedViewType !== currentFtViewType) {
                canvasActions.updateToolsForViewType(canvasEnvironment);
            }
        }
    }, [canvasEnvironment, lazyLoadDepth, loadAllFT, currentFtViewType, isActive]);

    useEffect(() => {
        if (isActive && canvasEnvironment && canvasEnvironment.eventSequence && canvasEnvironment.eventSequence.id !== 0) {
            const [element] = canvasEnvironment.selection;
            // Let the top menu knows the selected node:
            // The Top menu needs to know in order to enable/disable menu items accordingly
            setCurrentCanvasSelectedNode(element ? element.get('data') : null);
        }
    }, [isActive, canvasEnvironment]);

    // Register search functions with context when tab is active
    useEffect(() => {
        // Only register when this canvas is active - don't clear when inactive
        // because another canvas might be active and have registered its functions
        if (isActive && canvasEnvironment) {
            setCanvasSearchFunction(() => (query: string) => canvasActions.searchAllNodes(canvasEnvironment, query));
            setCanvasSearchByTagFunction(() => (query: string) => canvasActions.searchAllNodesByTag(canvasEnvironment, query));
            setCanvasNavigateFunction(() => (elementId: string) => canvasActions.navigateToNode(canvasEnvironment, elementId));
        }
        // No cleanup needed - the next active canvas will overwrite,
        // or if no canvas is active, that's fine too
    }, [isActive, canvasEnvironment, eventSequence?.name, setCanvasSearchFunction, setCanvasSearchByTagFunction, setCanvasNavigateFunction]);

    // Update profiler's active ESD name when tab becomes active
    const { setActiveEsdName } = useProfiler() || {};
    useEffect(() => {
        if (isActive && eventSequence?.name && setActiveEsdName) {
            setActiveEsdName(eventSequence.name);
        }
    }, [isActive, eventSequence?.name, setActiveEsdName]);

    return (
        <div style={isCanvasFullscreen ? {
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 1300, backgroundColor: '#fff',
            display: 'flex', flexDirection: 'column',
        } : { display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
            <div ref={containerRef} className="canvas" style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isRunning}>
                    <CircularProgress color='inherit' />
                </Backdrop>

                <FaultTreeContextMenu elementView={anchorEl} open={open} handleClose={closeContextMenu} env={canvasEnvironment} />
                <AddEndEventModal open={modals.addEndEvent && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('addEndEvent')} />
                <AddEventFaultTreeModal open={modals.addEventFaultTree && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('addEventFaultTree')} />
                <AddFaultTreeNodeModal open={modals.addEventFaultTreeNode && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('addEventFaultTreeNode')} />
                <AddPivotalEventModal open={modals.addPivotalEvent && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('addPivotalEvent')} />
                <DeleteEventModal open={modals.deleteEvent && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('deleteEvent')} />
                <DeleteEventFaultTreeModal open={modals.deleteEventFaultTree && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('deleteEventFaultTree')} />
                <DeleteFaultTreeNodeModal open={modals.deleteFaultTreeNode && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('deleteFaultTreeNode')} />
                <EditEndEventModal open={modals.editEndEvent && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('editEndEvent')} />
                <EditInitiatingEventModal open={modals.editInitiatingEvent && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('editInitiatingEvent')} />
                <EditPivotalEventModal open={modals.editPivotalEvent && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('editPivotalEvent')} />
                <EditEventFaultTreeModal open={modals.editEventFaultTree && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('editEventFaultTree')} />
                <EditFaultTreeNodeModal open={modals.editFaultTreeNode && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('editFaultTreeNode')} />
                <QuantifyFaultTreeModal open={modals.quantifyFaultTree && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('quantifyFaultTree')} />
                <ReassignParentEventModal open={modals.reassignParent && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('reassignParent')} />
                <ReorderChildrenModal open={modals.reorderChildren && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('reorderChildren')} />
                <TagsComponentModal open={modals.tags && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('tags')} />
                <CalloutComponentModal open={modals.callout && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('callout')} />
                <DeleteCalloutComponentModal open={modals.deleteCallout && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('deleteCallout')} />
                <OdnaCommentComponentModal open={modals.odnaComment && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('odnaComment')} />
                <OdnaStatComponentModal open={modals.odnaStat && canvasEnvironment != null} env={canvasEnvironment} onClose={(): void => closeModal('odnaStat')} />
                <NodeOccurrenceListComponentModal open={modals.nodeOccurrenceList && canvasEnvironment != null} env={canvasEnvironment} onClose={(): void => closeModal('nodeOccurrenceList')} />
                <AccidentDnaSummaryComponentModal open={modals.accidentDNASummary && canvasEnvironment != null} env={canvasEnvironment} onClose={(): void => closeModal('accidentDNASummary')} />
                <PsfCodeSelectorModal open={modals.psfCodeSelector && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('psfCodeSelector')} />
                <PsfCountTableModal open={modals.psfTableCount && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('psfTableCount')} />
                <SelectComponentModal open={modals.selectComponent && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('selectComponent')} />
                <CFRLookUpNodeModal open={modals.cfrLookup && canvasEnvironment != null && canvasEnvironment.selection[0]?.get('data') != null} env={canvasEnvironment} onClose={(): void => closeModal('cfrLookup')} />
            </div>
            <ModelTestingPanel open={modals.modelTesting && canvasEnvironment != null} scope={canvasEnvironment?.eventSequence?.id != null ? {kind: 'eventSequence', id: canvasEnvironment.eventSequence.id} : null} onClose={(): void => closeModal('modelTesting')} initialTab={modelTestingTab} />
            {mcsOpen && (
                <div style={{
                    height: mcsPanelCollapsed ? MCS_HEADER_HEIGHT : mcsPanelHeight,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    borderTop: '1px solid #ccc',
                }}>
                    {/* Resize handle */}
                    {!mcsPanelCollapsed && (
                        <div
                            onMouseDown={handleMcsResizeMouseDown}
                            style={{
                                height: 5,
                                cursor: 'row-resize',
                                background: 'transparent',
                                flexShrink: 0,
                            }}
                            onMouseOver={(e): void => { (e.currentTarget as HTMLDivElement).style.background = 'rgba(0,0,0,0.12)'; }}
                            onMouseOut={(e): void => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                        />
                    )}
                    <MinimalCutSetsPanel
                        open={mcsOpen}
                        env={canvasEnvironment}
                        onClose={(): void => closeModal('minimalCutSets')}
                        collapsed={mcsPanelCollapsed}
                        onToggleCollapse={(): void => setMcsPanelCollapsed((prev) => !prev)}
                        resetKey={mcsResetKey}
                        initialTab={mcsInitialTab}
                    />
                </div>
            )}
        </div>
    );
}


